import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { useDebounced } from '../kits/KitEditor';
import { dayNumber, eventDays, eventName, eventsListUrl, heading, monthGrid, rangeFor, shiftAnchor, startOfMonth, todayInWarsaw, WEEKDAYS_SHORT, withDates, type CalendarEvent, type DatedEvent } from './calendar';
import { EventList } from './EventList';
import { useClientNames } from './EventsPage';
import { addDays, isClosed } from './labels';
import './picker.css';

/** How many events a day of the small month shows before "+N". */
const PER_DAY = 3;

/**
 * Choosing another event (owner 2026-10-21 06:58, "Kopiuj z wydarzenia": "data z opisem wydarzenia albo wybór z
 * kalendarza naszego najlepiej"): "Kalendarz" first — a small month, a click on an event picks it — or "Lista" with
 * a search (date, name, client, place). Only events the user may see (the API's own scope); `excludeId` (the event
 * itself) is left out.
 */
export function EventPicker({ excludeId, onPick, label = 'Wybierz wydarzenie' }: { excludeId?: string; onPick: (e: DatedEvent) => void; label?: string }) {
  const [mode, setMode] = useState<'calendar' | 'list'>('calendar');
  return (
    <div className="event-picker" data-feature-id="packlists.copy_from">
      <div role="tablist" aria-label={label} className="segment">
        <button role="tab" type="button" aria-selected={mode === 'calendar'} onClick={() => setMode('calendar')}>
          Kalendarz
        </button>
        <button role="tab" type="button" aria-selected={mode === 'list'} onClick={() => setMode('list')}>
          Lista
        </button>
      </div>
      {mode === 'calendar' ? <MiniMonth excludeId={excludeId} onPick={onPick} /> : <SearchList excludeId={excludeId} onPick={onPick} />}
    </div>
  );
}

function useEvents(url: string) {
  return useQuery({ queryKey: ['events', 'picker', url], queryFn: () => api<{ events: CalendarEvent[]; truncated: boolean }>(url) });
}

function MiniMonth({ excludeId, onPick }: { excludeId?: string | undefined; onPick: (e: DatedEvent) => void }) {
  const [anchor, setAnchor] = useState(() => startOfMonth(todayInWarsaw()));
  const [openDay, setOpenDay] = useState<string | null>(null);
  const range = rangeFor('month', anchor)!;
  const list = useEvents(eventsListUrl({ q: '', status: '', range }));
  const events = withDates((list.data?.events ?? []).filter((e) => e.id !== excludeId && !isClosed(e.status)));
  const onDay = (day: string) => events.filter((e) => {
    const d = eventDays(e);
    return d.first <= day && day <= d.last;
  });
  const month = anchor.slice(0, 7);
  const today = todayInWarsaw();
  const go = (dir: 1 | -1) => {
    setAnchor(shiftAnchor('month', anchor, dir));
    setOpenDay(null);
  };
  const dayEvents = openDay ? onDay(openDay) : [];
  return (
    <div className="mini-month">
      <div className="mini-month-head">
        <button className="btn ghost small" type="button" aria-label="Poprzedni miesiąc" onClick={() => go(-1)}>
          ‹
        </button>
        <strong aria-live="polite">{heading('month', anchor)}</strong>
        <button className="btn ghost small" type="button" aria-label="Następny miesiąc" onClick={() => go(1)}>
          ›
        </button>
      </div>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać wydarzeń'}</p>}
      <table className="mini-month-grid" aria-label={`Wydarzenia: ${heading('month', anchor)}`}>
        <thead>
          <tr>
            {WEEKDAYS_SHORT.map((d) => (
              <th key={d} scope="col">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthGrid(anchor).map((week) => (
            <tr key={week[0]}>
              {week.map((day) => {
                const items = onDay(day);
                return (
                  <td key={day} className={[day.slice(0, 7) !== month ? 'other' : '', day === today ? 'today' : ''].filter(Boolean).join(' ') || undefined}>
                    <span className="mini-day">{dayNumber(day)}</span>
                    {items.slice(0, PER_DAY).map((e) => (
                      <button key={e.id} type="button" className="mini-event" data-status={e.status} title={eventName(e)} onClick={() => onPick(e)}>
                        {eventName(e)}
                      </button>
                    ))}
                    {items.length > PER_DAY && (
                      <button type="button" className="mini-more" aria-label={`Wszystkie wydarzenia ${day}`} onClick={() => setOpenDay(day)}>
                        +{items.length - PER_DAY}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {openDay && (
        <div className="mini-day-list">
          <p className="muted small">Wydarzenia {openDay.split('-').reverse().join('.')}:</p>
          <PickList events={dayEvents} onPick={onPick} />
        </div>
      )}
    </div>
  );
}

function SearchList({ excludeId, onPick }: { excludeId?: string | undefined; onPick: (e: DatedEvent) => void }) {
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim(), 250);
  // without a search: half a year back and a quarter ahead, the newest first (copies are made from recent events)
  const today = todayInWarsaw();
  const list = useEvents(eventsListUrl({ q: search, status: '', range: search ? null : { from: addDays(today, -183), to: addDays(today, 92) } }));
  const events = withDates((list.data?.events ?? []).filter((e) => e.id !== excludeId)).sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  return (
    <div className="ev-pick-list">
      <label className="field">
        <span>Szukaj (nazwa, miejsce, klient)</span>
        <input type="search" value={q} autoFocus onChange={(e) => setQ(e.target.value)} placeholder="np. Sfinks" autoComplete="off" />
      </label>
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {list.data && <PickList events={events} onPick={onPick} empty={search ? 'Nie znaleziono wydarzeń.' : 'Brak wydarzeń w ostatnich miesiącach.'} />}
      {list.data?.truncated && <p className="muted small">Pokazano pierwsze 200 — zawęź wyszukiwanie.</p>}
    </div>
  );
}

function PickList({ events, onPick, empty = 'Brak wydarzeń.' }: { events: DatedEvent[]; onPick: (e: DatedEvent) => void; empty?: string }) {
  const names = useClientNames();
  const byId = new Map(events.map((e) => [e.id, e]));
  return <EventList events={events} names={names} selectedId={null} onOpen={(id) => byId.has(id) && onPick(byId.get(id)!)} empty={empty} label="Wydarzenia do wyboru" />;
}
